import React, { useState, useEffect } from 'react';
import { UserMemory, useMemory, useUpdateMemory } from '../../../../api/memory';
import { Feedback } from '@plerionhq/design-system';
import { LoadingState } from '../../../common/LoadingState';
import { Button } from '@plerionhq/design-system';
import {
  ROLE_OPTIONS,
  DETAIL_LEVEL_OPTIONS,
  roleLabel,
  detailLevelText,
} from '../../../onboarding-new/steps/AboutYouSubSteps';

import { useTranslation } from 'react-i18next';

export const UserMemoryComponent: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  // The role and detail-level labels live with the onboarding flow that writes them.
  const { t: tOnboarding } = useTranslation('pleri/onboarding');
  const { data: memoryResponse, isLoading, error } = useMemory();
  const updateMemoryMutation = useUpdateMemory();
  const [localMemory, setLocalMemory] = useState<
    Record<string, string | undefined>
  >({});
  // Update local state when data is loaded
  useEffect(() => {
    if (memoryResponse) {
      setLocalMemory({
        ...memoryResponse.memory,
        otherNotes: memoryResponse?.memory?.otherNotes?.join('\n'),
      });
    }
  }, [memoryResponse]);

  const handleFieldChange = (field: keyof UserMemory, value: string) => {
    const updatedMemory = { ...localMemory, [field]: value };
    setLocalMemory(updatedMemory);
  };

  const handleSave = async () => {
    const trimmedRole = (localMemory.role ?? '').trim();
    const trimmedCommunication = (
      localMemory.communicationPreference ?? ''
    ).trim();
    await updateMemoryMutation.mutateAsync({
      memory: {
        ...localMemory,
        role: trimmedRole || 'something-else',
        communicationPreference: trimmedCommunication || undefined,
        otherNotes:
          typeof localMemory?.otherNotes === 'string'
            ? localMemory.otherNotes.split('\n').filter((s) => s.trim())
            : undefined,
      },
    });
  };

  const [roleShowAll, setRoleShowAll] = useState(false);

  const roleValue = localMemory.role ?? '';
  const isRoleSomethingElse =
    roleValue === 'something-else' ||
    (roleValue.length > 0 &&
      !ROLE_OPTIONS.some((o) => o.id === roleValue || o.label === roleValue));
  const communicationValue = localMemory.communicationPreference ?? '';

  const ROLE_VISIBLE_COUNT = 3;
  const roleOptionsToShow = roleShowAll
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.slice(0, ROLE_VISIBLE_COUNT);
  const roleRemainingCount = ROLE_OPTIONS.length - ROLE_VISIBLE_COUNT;

  const bubbleButtonClass = (selected: boolean) =>
    `rounded-full px-2 py-1 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-1 ${
      selected
        ? 'bg-orange-100 dark:bg-gray-700 text-orange-700'
        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-orange-50 hover:text-orange-700'
    }`;

  const questionLabelClass =
    'mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300';
  const inputClass =
    'w-full rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-orange-400 focus:ring-1 focus:ring-orange-400';
  const textareaClass = `${inputClass} mt-2 min-h-[100px] resize-y`;

  if (isLoading) {
    return <LoadingState text={t('common:loading')} />;
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Feedback variant="error">
          {t('somethingWentWrongLoadingYourProfilePlease')}
        </Feedback>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-4 pt-6">
        <div className="mx-auto flex w-full max-w-[600px] justify-center px-6">
          <div className="w-full">
            <p className="mb-2 px-4 text-left text-gray-600 dark:text-gray-400">
              {t('shareABitAboutYourselfToHelp')}
            </p>
            {/* Nickname */}
            <div className="border-lighter-gray bg-action-secondary mb-2 px-4 py-3">
              <span className={questionLabelClass}>
                {t('whatShouldICallYou')}
              </span>
              <input
                aria-label={t('whatShouldICallYou')}
                value={localMemory.nickName ?? ''}
                onChange={(e) => handleFieldChange('nickName', e.target.value)}
                placeholder={t('yourNameOrNickname')}
                className={`mt-2 ${inputClass}`}
                data-testid="nickName"
              />
            </div>

            {/* Role – bubbles + input */}
            <div className="border-lighter-gray bg-action-secondary mb-2 px-4 py-3">
              <span className={questionLabelClass}>{t('whatYourRole')}</span>
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                {roleOptionsToShow.map((option) => {
                  const optionWithDesc = option as {
                    id: string;
                    label: string;
                    description?: string;
                  };
                  const valueToSet =
                    optionWithDesc.id === 'something-else'
                      ? 'something-else'
                      : (optionWithDesc.description ?? optionWithDesc.label);
                  const selected =
                    !isRoleSomethingElse &&
                    (roleValue === optionWithDesc.id ||
                      roleValue === optionWithDesc.label ||
                      roleValue === optionWithDesc.description);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleFieldChange('role', valueToSet)}
                      className={bubbleButtonClass(selected)}
                      data-testid={`role-bubble-${option.id}`}
                    >
                      {roleLabel(tOnboarding, option.id)}
                    </button>
                  );
                })}
                {roleRemainingCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setRoleShowAll(!roleShowAll)}
                    className="rounded border-0 px-1 py-0.5 text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline"
                    data-testid="role-show-all-options"
                  >
                    {roleShowAll
                      ? t('showLess')
                      : t('showMore', { roleRemainingCount })}
                  </button>
                )}
              </div>
              <input
                aria-label={t('whatYourRole')}
                value={roleValue === 'something-else' ? '' : roleValue}
                onChange={(e) => handleFieldChange('role', e.target.value)}
                placeholder={t('orTypeYourRole')}
                className={`mt-2 ${inputClass}`}
                data-testid="about-you-role-input"
              />
            </div>

            {/* Communication preference – bubbles + input */}
            <div className="border-lighter-gray bg-action-secondary mb-2 px-4 py-3">
              <span className={questionLabelClass}>
                {t('howDoYouPreferToCommunicate')}
              </span>
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                {DETAIL_LEVEL_OPTIONS.map((option) => {
                  const valueToSet = option.description ?? option.label;
                  const selected =
                    communicationValue === option.id ||
                    communicationValue === option.label ||
                    communicationValue === option.description;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() =>
                        handleFieldChange('communicationPreference', valueToSet)
                      }
                      className={bubbleButtonClass(selected)}
                      data-testid={`communication-bubble-${option.id}`}
                    >
                      {detailLevelText(tOnboarding, option.id).label}
                    </button>
                  );
                })}
              </div>
              <input
                aria-label={t('howDoYouPreferToCommunicate')}
                value={communicationValue}
                onChange={(e) =>
                  handleFieldChange('communicationPreference', e.target.value)
                }
                placeholder={t('orDescribeHowYouPreferToCommunicate')}
                className={`mt-2 ${inputClass}`}
                data-testid="about-you-communication-input"
              />
            </div>

            {/* Other notes – textarea */}
            <div className="border-lighter-gray bg-action-secondary mb-2 px-4 py-3">
              <span className={questionLabelClass}>
                {t('otherThingsToSaveInMemory')}
              </span>
              <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                {t('eGToolsYouUseDomainsYou')}
              </p>
              <textarea
                aria-label={t('otherThingsToSaveInMemory')}
                value={
                  typeof localMemory.otherNotes === 'string'
                    ? localMemory.otherNotes
                    : (localMemory.otherNotes ?? '')
                }
                onChange={(e) =>
                  handleFieldChange('otherNotes', e.target.value)
                }
                rows={4}
                className={textareaClass}
                data-testid="otherNotes"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="sticky bottom-0 flex-shrink-0 border-t border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="flex w-full flex-col gap-2">
          {updateMemoryMutation.error ? (
            <Feedback variant="error">
              {t('failedToSavePleaseTryAgain')}
            </Feedback>
          ) : null}
          {updateMemoryMutation.isSuccess ? (
            <Feedback variant="success">{t('savedSuccessfully')}</Feedback>
          ) : null}
          <div className="flex w-full justify-end font-sans">
            <Button
              variant="primary"
              className="mx-0 mt-0 w-auto px-4 py-1.5 text-sm"
              data-testid="save-about-you"
              isLoading={updateMemoryMutation.isPending}
              label={t('common:save')}
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserMemoryComponent;
